import React, { useEffect, useMemo, useState } from 'react';
import { iceDownload, iceFetchJson } from './iceApi';
import { Button, GoalCard, KpiRow, SelectField, TextArea, TextField } from './iceUi';

function toNumberOrNull(s) {
  const t = String(s ?? '').trim();
  if (!t) return null;
  const v = Number(t);
  return Number.isFinite(v) ? v : null;
}

function toIntOrNull(s) {
  const n = toNumberOrNull(s);
  if (n == null) return null;
  const i = Math.trunc(n);
  return Number.isFinite(i) ? i : null;
}

function toIsoOrNull(date, time) {
  const d = String(date || '').trim();
  if (!d) return null;
  const t = String(time || '').trim() || '00:00';
  const iso = `${d}T${t}:00`; // lokal tid; toISOString nedan ger rätt UTC (F6, förut falskt Z)
  try {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toISOString();
  } catch {
    return null;
  }
}

function Section({ title, children, right }) {
  return (
    <div className="rounded border border-slate-200 bg-white">
      <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between gap-3">
        <div className="text-sm font-semibold">{title}</div>
        {right ? <div className="flex items-center gap-2">{right}</div> : null}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

export default function IceAdminPanel() {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [goals, setGoals] = useState([]);
  const [selectedGoal, setSelectedGoal] = useState(null);

  const [kpis, setKpis] = useState([]);
  const [selectedKpi, setSelectedKpi] = useState(null);

  // Goal editor
  const [goalCode, setGoalCode] = useState('');
  const [goalTitle, setGoalTitle] = useState('');
  const [goalOwnerRole, setGoalOwnerRole] = useState('');
  const [goalDescription, setGoalDescription] = useState('');

  // New goal
  const [newGoalCode, setNewGoalCode] = useState('');
  const [newGoalTitle, setNewGoalTitle] = useState('');

  // KPI editor
  const [kpiName, setKpiName] = useState('');
  const [kpiUnit, setKpiUnit] = useState('');
  const [kpiTarget, setKpiTarget] = useState('');
  const [kpiYellow, setKpiYellow] = useState('');
  const [kpiRed, setKpiRed] = useState('');
  const [kpiDirection, setKpiDirection] = useState('HIGHER_IS_BETTER');
  const [kpiFrequency, setKpiFrequency] = useState('');
  const [kpiSortOrder, setKpiSortOrder] = useState('');
  const [kpiStatus, setKpiStatus] = useState('draft');

  // New KPI
  const [newKpiName, setNewKpiName] = useState('');

  // Measurements
  const [measurements, setMeasurements] = useState([]);
  const [mDate, setMDate] = useState('');
  const [mTime, setMTime] = useState('');
  const [mValue, setMValue] = useState('');
  const [mNote, setMNote] = useState('');

  // Actions
  const [actions, setActions] = useState([]);
  const [newActionTitle, setNewActionTitle] = useState('');
  const [newActionOwnerRole, setNewActionOwnerRole] = useState('');
  const [newActionDueDate, setNewActionDueDate] = useState('');
  const [newActionDueTime, setNewActionDueTime] = useState('');
  const [newActionDescription, setNewActionDescription] = useState('');

  // External tokens
  const [tokens, setTokens] = useState([]);
  const [newTokenLabel, setNewTokenLabel] = useState('');
  const [newTokenDays, setNewTokenDays] = useState('30');
  const [latestTokenUrl, setLatestTokenUrl] = useState('');

  // Report
  const [report, setReport] = useState(null);
  const [includeUnpublished, setIncludeUnpublished] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(true);
  const [limitGoals, setLimitGoals] = useState('10');

  async function reloadGoals(selectId = null) {
    const data = await iceFetchJson('/tenants/current/ice/goals', {}, { errorMessage: 'Kunde inte hämta mål' });
    const items = Array.isArray(data?.items) ? data.items : [];
    setGoals(items);

    const picked = selectId != null ? items.find((g) => Number(g.id) === Number(selectId)) : (selectedGoal ? items.find((g) => Number(g.id) === Number(selectedGoal.id)) : null);
    setSelectedGoal(picked || items[0] || null);
  }

  async function reloadKpis(goalId) {
    if (!goalId) {
      setKpis([]);
      setSelectedKpi(null);
      return;
    }
    const data = await iceFetchJson(`/tenants/current/ice/kpis?goal_id=${encodeURIComponent(goalId)}`, {}, { errorMessage: 'Kunde inte hämta KPI:er' });
    const items = Array.isArray(data?.items) ? data.items : [];
    setKpis(items);

    if (selectedKpi) {
      const existing = items.find((k) => Number(k.id) === Number(selectedKpi.id));
      setSelectedKpi(existing || null);
    }
  }

  async function reloadMeasurements(kpiId) {
    if (!kpiId) {
      setMeasurements([]);
      return;
    }
    const data = await iceFetchJson(`/tenants/current/ice/measurements?kpi_id=${encodeURIComponent(kpiId)}`, {}, { errorMessage: 'Kunde inte hämta mätvärden' });
    setMeasurements(Array.isArray(data?.items) ? data.items : []);
  }

  async function reloadActions(kpiId) {
    if (!kpiId) {
      setActions([]);
      return;
    }
    const data = await iceFetchJson(`/tenants/current/ice/actions?kpi_id=${encodeURIComponent(kpiId)}`, {}, { errorMessage: 'Kunde inte hämta åtgärder' });
    setActions(Array.isArray(data?.items) ? data.items : []);
  }

  async function reloadTokens() {
    const data = await iceFetchJson('/tenants/current/ice/external-tokens', {}, { errorMessage: 'Kunde inte hämta externa tokens' });
    setTokens(Array.isArray(data?.items) ? data.items : []);
  }

  useEffect(() => {
    let alive = true;
    async function boot() {
      setBusy(true);
      setError('');
      try {
        await reloadGoals();
        await reloadTokens();
      } catch (e) {
        if (!alive) return;
        setError(String(e?.message || e));
      } finally {
        if (!alive) return;
        setBusy(false);
      }
    }
    boot();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedGoal) {
      setGoalCode('');
      setGoalTitle('');
      setGoalOwnerRole('');
      setGoalDescription('');
      setKpis([]);
      setSelectedKpi(null);
      return;
    }
    setGoalCode(String(selectedGoal.code || ''));
    setGoalTitle(String(selectedGoal.title || ''));
    setGoalOwnerRole(String(selectedGoal.owner_role || ''));
    setGoalDescription(String(selectedGoal.description_md || ''));
    reloadKpis(selectedGoal.id).catch((e) => setError(String(e?.message || e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGoal?.id]);

  useEffect(() => {
    if (!selectedKpi) {
      setKpiName('');
      setKpiUnit('');
      setKpiTarget('');
      setKpiYellow('');
      setKpiRed('');
      setKpiDirection('HIGHER_IS_BETTER');
      setKpiFrequency('');
      setKpiSortOrder('');
      setKpiStatus('draft');
      setMeasurements([]);
      setActions([]);
      return;
    }

    setKpiName(String(selectedKpi.name || ''));
    setKpiUnit(String(selectedKpi.unit || ''));
    setKpiTarget(selectedKpi.target_value != null ? String(selectedKpi.target_value) : '');
    setKpiYellow(selectedKpi.yellow_threshold != null ? String(selectedKpi.yellow_threshold) : '');
    setKpiRed(selectedKpi.red_threshold != null ? String(selectedKpi.red_threshold) : '');
    setKpiDirection(String(selectedKpi.direction || 'HIGHER_IS_BETTER'));
    setKpiFrequency(String(selectedKpi.frequency || ''));
    setKpiSortOrder(selectedKpi.sort_order != null ? String(selectedKpi.sort_order) : '');
    setKpiStatus(String(selectedKpi.status || 'draft'));

    reloadMeasurements(selectedKpi.id).catch((e) => setError(String(e?.message || e)));
    reloadActions(selectedKpi.id).catch((e) => setError(String(e?.message || e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKpi?.id]);

  const headerRight = useMemo(() => {
    const base = window.location?.origin || '';
    return latestTokenUrl ? (
      <div className="text-xs text-slate-700">
        Senast skapad extern länk: <span className="font-mono">{base}{latestTokenUrl}</span>
      </div>
    ) : null;
  }, [latestTokenUrl]);

  async function withBusy(fn) {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      {busy ? <div className="text-sm text-slate-600">Arbetar…</div> : null}
      {error ? <div className="text-sm text-red-700">{error}</div> : null}

      <Section
        title="Adminverktyg"
        right={
          <div className="flex items-center gap-2">
            <Button variant="ghost" disabled={busy} onClick={() => withBusy(async () => { await reloadGoals(); await reloadTokens(); })}>Uppdatera</Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-2">
            <div className="text-xs font-semibold text-slate-700">Management review</div>
            <div className="grid grid-cols-3 gap-2">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={includeUnpublished} onChange={(e) => setIncludeUnpublished(e.target.checked)} />
                Include unpublished
              </label>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
                Include archived
              </label>
              <TextField label="limit_goals" value={limitGoals} onChange={setLimitGoals} />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                disabled={busy}
                onClick={() =>
                  withBusy(async () => {
                    const q = new URLSearchParams();
                    q.set('include_unpublished', String(!!includeUnpublished));
                    q.set('include_archived', String(!!includeArchived));
                    q.set('limit_goals', String(toIntOrNull(limitGoals) || 5));
                    const data = await iceFetchJson(`/tenants/current/ice/admin/management-review-report?${q.toString()}`);
                    setReport(data);
                  })
                }
              >
                Generera
              </Button>
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() =>
                  withBusy(async () => {
                    const q = new URLSearchParams();
                    q.set('include_unpublished', String(!!includeUnpublished));
                    q.set('include_archived', String(!!includeArchived));
                    q.set('limit_goals', String(toIntOrNull(limitGoals) || 5));
                    q.set('format', 'json');
                    await iceDownload(`/tenants/current/ice/admin/management-review-report?${q.toString()}`, { filenameFallback: 'ice-management-review.json' });
                  })
                }
              >
                Ladda ner JSON
              </Button>
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() =>
                  withBusy(async () => {
                    const q = new URLSearchParams();
                    q.set('include_unpublished', String(!!includeUnpublished));
                    q.set('include_archived', String(!!includeArchived));
                    q.set('limit_goals', String(toIntOrNull(limitGoals) || 5));
                    q.set('format', 'yaml');
                    await iceDownload(`/tenants/current/ice/admin/management-review-report?${q.toString()}`, { filenameFallback: 'ice-management-review.yaml' });
                  })
                }
              >
                Ladda ner YAML
              </Button>
            </div>
            {report ? (
              <div className="mt-2 text-xs text-slate-700">
                <div className="font-semibold">{report.report_type}</div>
                <div>top_risk_goals: {(report.top_risk_goals || []).length}</div>
                <div>negative_trend_kpis: {(report.negative_trend_kpis || []).length}</div>
                <div>open_actions: {(report.open_actions || []).length}</div>
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <div className="text-xs font-semibold text-slate-700">Export</div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() =>
                  withBusy(async () => {
                    await iceDownload(
                      '/tenants/current/ice/admin/export?format=json&include_unpublished=true&include_archived=false&include_actions=true',
                      { filenameFallback: 'ice-export.json' },
                    );
                  })
                }
              >
                Export JSON
              </Button>
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() =>
                  withBusy(async () => {
                    await iceDownload(
                      '/tenants/current/ice/admin/export?format=yaml&include_unpublished=true&include_archived=false&include_actions=true',
                      { filenameFallback: 'ice-export.yaml' },
                    );
                  })
                }
              >
                Export YAML
              </Button>
            </div>
            <div className="text-xs text-slate-600">Exporten innehåller goals + kpis (+ actions om valt). Measurements exporteras inte (MVP).</div>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-semibold text-slate-700">Extern delning</div>
            <div className="grid grid-cols-3 gap-2">
              <TextField label="Label" value={newTokenLabel} onChange={setNewTokenLabel} placeholder="t.ex. revisor" />
              <TextField label="expires_in_days" value={newTokenDays} onChange={setNewTokenDays} />
              <div className="flex items-end">
                <Button
                  variant="primary"
                  disabled={busy}
                  onClick={() =>
                    withBusy(async () => {
                      const days = toIntOrNull(newTokenDays) || 30;
                      const data = await iceFetchJson('/tenants/current/ice/external-tokens', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ label: newTokenLabel, expires_in_days: days }),
                      });
                      setLatestTokenUrl(String(data?.url || ''));
                      setNewTokenLabel('');
                      await reloadTokens();
                    })
                  }
                >
                  Skapa
                </Button>
              </div>
            </div>

            {headerRight}

            <div className="space-y-2">
              {tokens.length === 0 ? (
                <div className="text-xs text-slate-600">Inga tokens.</div>
              ) : (
                tokens.map((t) => (
                  <div key={t.id} className="rounded border border-slate-200 p-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs font-semibold text-slate-800">{t.label || t.id}</div>
                      <div className="flex items-center gap-2">
                        {!t.revoked_at ? (
                          <Button
                            variant="ghost"
                            disabled={busy}
                            onClick={() =>
                              withBusy(async () => {
                                await iceFetchJson(`/tenants/current/ice/external-tokens/${encodeURIComponent(t.id)}/revoke`, { method: 'POST' });
                                await reloadTokens();
                              })
                            }
                          >
                            Revoke
                          </Button>
                        ) : (
                          <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-700">revoked</span>
                        )}
                        <Button
                          variant="danger"
                          disabled={busy}
                          onClick={() =>
                            withBusy(async () => {
                              await iceFetchJson(`/tenants/current/ice/external-tokens/${encodeURIComponent(t.id)}`, { method: 'DELETE' });
                              await reloadTokens();
                            })
                          }
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-600">
                      created: {t.created_at || '-'} • expires: {t.expires_at || '-'} • last_used: {t.last_used_at || '-'}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-3">
          <Section title="Mål (admin)">
            <div className="space-y-2">
              {goals.length === 0 ? (
                <div className="text-sm text-slate-600">Inga mål ännu.</div>
              ) : (
                goals.map((g) => (
                  <GoalCard
                    key={g.id}
                    goal={g}
                    selected={selectedGoal?.id === g.id}
                    onSelect={(goal) => {
                      setSelectedGoal(goal);
                      setSelectedKpi(null);
                    }}
                  />
                ))
              )}
            </div>

            <div className="mt-3 pt-3 border-t border-slate-200">
              <div className="text-xs font-semibold text-slate-700 mb-2">Skapa nytt mål</div>
              <div className="grid grid-cols-2 gap-2">
                <TextField label="Code" value={newGoalCode} onChange={setNewGoalCode} placeholder="ICE-001" />
                <TextField label="Title" value={newGoalTitle} onChange={setNewGoalTitle} placeholder="Titel" />
              </div>
              <div className="mt-2">
                <Button
                  variant="primary"
                  disabled={busy}
                  onClick={() =>
                    withBusy(async () => {
                      const payload = { code: String(newGoalCode || '').trim() || null, title: String(newGoalTitle || '').trim() };
                      const data = await iceFetchJson('/tenants/current/ice/goals', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                      });
                      const goal = data?.goal;
                      setNewGoalCode('');
                      setNewGoalTitle('');
                      await reloadGoals(goal?.id);
                    })
                  }
                >
                  Skapa
                </Button>
              </div>
            </div>
          </Section>
        </div>

        <div className="space-y-3">
          <Section
            title="Valt mål"
            right={
              selectedGoal ? (
                <Button
                  variant="primary"
                  disabled={busy || String(selectedGoal.status || '') === 'published'}
                  onClick={() =>
                    withBusy(async () => {
                      await iceFetchJson(`/tenants/current/ice/goals/${encodeURIComponent(selectedGoal.id)}/publish`, { method: 'POST' });
                      await reloadGoals(selectedGoal.id);
                    })
                  }
                >
                  Publicera
                </Button>
              ) : null
            }
          >
            {!selectedGoal ? (
              <div className="text-sm text-slate-600">Välj ett mål.</div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <TextField label="Code" value={goalCode} onChange={setGoalCode} />
                  <TextField label="Owner role" value={goalOwnerRole} onChange={setGoalOwnerRole} placeholder="t.ex. KMA" />
                </div>
                <TextField label="Title" value={goalTitle} onChange={setGoalTitle} />
                <TextArea label="Description (md)" value={goalDescription} onChange={setGoalDescription} rows={4} />

                <div className="flex items-center gap-2 pt-2">
                  <Button
                    variant="primary"
                    disabled={busy}
                    onClick={() =>
                      withBusy(async () => {
                        const payload = {
                          code: String(goalCode || '').trim() || null,
                          title: String(goalTitle || '').trim(),
                          owner_role: String(goalOwnerRole || '').trim() || null,
                          description_md: String(goalDescription || ''),
                        };
                        const data = await iceFetchJson(`/tenants/current/ice/goals/${encodeURIComponent(selectedGoal.id)}`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(payload),
                        });
                        const updated = data?.goal;
                        await reloadGoals(updated?.id || selectedGoal.id);
                      })
                    }
                  >
                    Spara
                  </Button>
                </div>
              </div>
            )}
          </Section>

          <Section title="KPI:er (för valt mål)">
            {!selectedGoal ? (
              <div className="text-sm text-slate-600">Välj ett mål.</div>
            ) : (
              <>
                <div className="space-y-2">
                  {kpis.length === 0 ? (
                    <div className="text-sm text-slate-600">Inga KPI:er.</div>
                  ) : (
                    kpis.map((k) => (
                      <KpiRow key={k.id} kpi={k} selected={selectedKpi?.id === k.id} onSelect={setSelectedKpi} />
                    ))
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-slate-200">
                  <div className="text-xs font-semibold text-slate-700 mb-2">Skapa ny KPI</div>
                  <div className="grid grid-cols-1 gap-2">
                    <TextField label="Name" value={newKpiName} onChange={setNewKpiName} placeholder="KPI – t.ex. H1" />
                    <Button
                      variant="primary"
                      disabled={busy}
                      onClick={() =>
                        withBusy(async () => {
                          const payload = {
                            goal_id: Number(selectedGoal.id),
                            name: String(newKpiName || '').trim(),
                            unit: null,
                            target_value: null,
                            yellow_threshold: null,
                            red_threshold: null,
                            direction: 'HIGHER_IS_BETTER',
                            frequency: null,
                            sort_order: null,
                          };
                          const data = await iceFetchJson('/tenants/current/ice/kpis', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(payload),
                          });
                          setNewKpiName('');
                          await reloadKpis(selectedGoal.id);
                          const created = data?.kpi;
                          if (created?.id) setSelectedKpi(created);
                        })
                      }
                    >
                      Skapa
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Section>
        </div>

        <div className="space-y-3">
          <Section
            title="Vald KPI"
            right={
              selectedKpi ? (
                <Button
                  variant="primary"
                  disabled={busy || String(selectedKpi.status || '') === 'published'}
                  onClick={() =>
                    withBusy(async () => {
                      await iceFetchJson(`/tenants/current/ice/kpis/${encodeURIComponent(selectedKpi.id)}/publish`, { method: 'POST' });
                      await reloadKpis(selectedGoal?.id);
                    })
                  }
                >
                  Publicera
                </Button>
              ) : null
            }
          >
            {!selectedKpi ? (
              <div className="text-sm text-slate-600">Välj en KPI.</div>
            ) : (
              <div className="space-y-2">
                <TextField label="Name" value={kpiName} onChange={setKpiName} />

                <div className="grid grid-cols-2 gap-2">
                  <TextField label="Unit" value={kpiUnit} onChange={setKpiUnit} placeholder="%" />
                  <TextField label="Frequency" value={kpiFrequency} onChange={setKpiFrequency} placeholder="weekly" />
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <TextField label="Target" value={kpiTarget} onChange={setKpiTarget} placeholder="100" />
                  <TextField label="Yellow" value={kpiYellow} onChange={setKpiYellow} placeholder="90" />
                  <TextField label="Red" value={kpiRed} onChange={setKpiRed} placeholder="80" />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <SelectField
                    label="Direction"
                    value={kpiDirection}
                    onChange={setKpiDirection}
                    options={[
                      { value: 'HIGHER_IS_BETTER', label: 'HIGHER_IS_BETTER' },
                      { value: 'LOWER_IS_BETTER', label: 'LOWER_IS_BETTER' },
                      { value: 'TARGET_RANGE', label: 'TARGET_RANGE' },
                    ]}
                  />
                  <TextField label="Sort order" value={kpiSortOrder} onChange={setKpiSortOrder} placeholder="10" />
                </div>

                <SelectField
                  label="Status"
                  value={kpiStatus}
                  onChange={setKpiStatus}
                  options={[
                    { value: 'draft', label: 'draft' },
                    { value: 'archived', label: 'archived' },
                    { value: 'published', label: 'published (set via publish)' },
                  ]}
                />

                <div className="flex items-center gap-2 pt-2">
                  <Button
                    variant="primary"
                    disabled={busy}
                    onClick={() =>
                      withBusy(async () => {
                        const payload = {
                          name: String(kpiName || '').trim(),
                          unit: String(kpiUnit || '').trim() || null,
                          target_value: toNumberOrNull(kpiTarget),
                          yellow_threshold: toNumberOrNull(kpiYellow),
                          red_threshold: toNumberOrNull(kpiRed),
                          direction: String(kpiDirection || 'HIGHER_IS_BETTER'),
                          frequency: String(kpiFrequency || '').trim() || null,
                          sort_order: toIntOrNull(kpiSortOrder),
                          status: kpiStatus === 'published' ? undefined : String(kpiStatus || 'draft'),
                        };

                        // Only include status if not "published" (publish endpoint should be used)
                        if (payload.status === undefined) delete payload.status;

                        const data = await iceFetchJson(`/tenants/current/ice/kpis/${encodeURIComponent(selectedKpi.id)}`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(payload),
                        });

                        const updated = data?.kpi;
                        await reloadKpis(selectedGoal?.id);
                        if (updated?.id) setSelectedKpi({ ...selectedKpi, ...updated });
                      })
                    }
                  >
                    Spara
                  </Button>
                </div>

                <div className="text-xs text-slate-600">
                  Tips: Lämna tröskelfält tomt för att spara som <span className="font-mono">null</span> (rensas via PATCH).
                </div>
              </div>
            )}
          </Section>

          <Section title="Mätvärden">
            {!selectedKpi ? (
              <div className="text-sm text-slate-600">Välj en KPI.</div>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-2">
                  <TextField label="Date" value={mDate} onChange={setMDate} placeholder="YYYY-MM-DD" />
                  <TextField label="Time" value={mTime} onChange={setMTime} placeholder="HH:MM" />
                  <TextField label="Value" value={mValue} onChange={setMValue} placeholder="85" />
                  <TextField label="Note" value={mNote} onChange={setMNote} placeholder="valfritt" />
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Button
                    variant="primary"
                    disabled={busy}
                    onClick={() =>
                      withBusy(async () => {
                        const payload = {
                          kpi_id: Number(selectedKpi.id),
                          measured_at: toIsoOrNull(mDate, mTime),
                          value: Number(String(mValue || '').trim()),
                          note: String(mNote || '').trim() || null,
                        };
                        await iceFetchJson('/tenants/current/ice/measurements', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(payload),
                        });
                        setMValue('');
                        setMNote('');
                        await reloadMeasurements(selectedKpi.id);
                        await reloadKpis(selectedGoal?.id);
                      })
                    }
                  >
                    Lägg till
                  </Button>
                </div>

                <div className="mt-3 space-y-2">
                  {measurements.length === 0 ? (
                    <div className="text-sm text-slate-600">Inga mätvärden.</div>
                  ) : (
                    measurements.map((m) => (
                      <div key={m.id} className="rounded border border-slate-200 p-2">
                        <div className="text-sm font-semibold">{m.value}</div>
                        <div className="text-xs text-slate-600">
                          {m.measured_at || '-'}
                          {m.note ? ` • ${m.note}` : ''}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </Section>

          <Section title="Åtgärder">
            {!selectedKpi ? (
              <div className="text-sm text-slate-600">Välj en KPI.</div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <TextField label="Title" value={newActionTitle} onChange={setNewActionTitle} placeholder="Åtgärd – ..." />
                  <TextField label="Owner role" value={newActionOwnerRole} onChange={setNewActionOwnerRole} placeholder="KMA" />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <TextField label="Due date" value={newActionDueDate} onChange={setNewActionDueDate} placeholder="YYYY-MM-DD" />
                  <TextField label="Due time" value={newActionDueTime} onChange={setNewActionDueTime} placeholder="HH:MM" />
                </div>
                <TextArea label="Description (md)" value={newActionDescription} onChange={setNewActionDescription} rows={3} className="mt-2" />
                <div className="mt-2">
                  <Button
                    variant="primary"
                    disabled={busy}
                    onClick={() =>
                      withBusy(async () => {
                        const payload = {
                          kpi_id: Number(selectedKpi.id),
                          title: String(newActionTitle || '').trim(),
                          owner_role: String(newActionOwnerRole || '').trim() || null,
                          due_at: toIsoOrNull(newActionDueDate, newActionDueTime),
                          description_md: String(newActionDescription || '').trim() || null,
                        };
                        await iceFetchJson('/tenants/current/ice/actions', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(payload),
                        });
                        setNewActionTitle('');
                        setNewActionOwnerRole('');
                        setNewActionDueDate('');
                        setNewActionDueTime('');
                        setNewActionDescription('');
                        await reloadActions(selectedKpi.id);
                      })
                    }
                  >
                    Skapa
                  </Button>
                </div>

                <div className="mt-3 space-y-2">
                  {actions.length === 0 ? (
                    <div className="text-sm text-slate-600">Inga åtgärder.</div>
                  ) : (
                    actions.map((a) => (
                      <div key={a.id} className="rounded border border-slate-200 p-2">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold">{a.title}</div>
                            <div className="text-xs text-slate-600">
                              {a.status ? `status: ${a.status}` : ''}
                              {a.state ? ` • state: ${a.state}` : ''}
                              {a.owner_role ? ` • owner: ${a.owner_role}` : ''}
                              {a.due_at ? ` • due: ${a.due_at}` : ''}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {String(a.status || '') !== 'published' ? (
                              <Button
                                variant="ghost"
                                disabled={busy}
                                onClick={() =>
                                  withBusy(async () => {
                                    await iceFetchJson(`/tenants/current/ice/actions/${encodeURIComponent(a.id)}/publish`, { method: 'POST' });
                                    await reloadActions(selectedKpi.id);
                                  })
                                }
                              >
                                Publicera
                              </Button>
                            ) : null}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 mt-2">
                          <SelectField
                            label="State"
                            value={String(a.state || 'open')}
                            onChange={(v) =>
                              withBusy(async () => {
                                await iceFetchJson(`/tenants/current/ice/actions/${encodeURIComponent(a.id)}`, {
                                  method: 'PATCH',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ state: v }),
                                });
                                await reloadActions(selectedKpi.id);
                              })
                            }
                            options={[
                              { value: 'open', label: 'open' },
                              { value: 'in_progress', label: 'in_progress' },
                              { value: 'done', label: 'done' },
                              { value: 'wontfix', label: 'wontfix' },
                            ]}
                          />
                          <SelectField
                            label="Status"
                            value={String(a.status || 'draft')}
                            onChange={(v) =>
                              withBusy(async () => {
                                await iceFetchJson(`/tenants/current/ice/actions/${encodeURIComponent(a.id)}`, {
                                  method: 'PATCH',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ status: v }),
                                });
                                await reloadActions(selectedKpi.id);
                              })
                            }
                            options={[
                              { value: 'draft', label: 'draft' },
                              { value: 'archived', label: 'archived' },
                              { value: 'published', label: 'published (set via publish)' },
                            ]}
                          />
                          <Button
                            variant="ghost"
                            disabled={busy}
                            onClick={() =>
                              withBusy(async () => {
                                await reloadActions(selectedKpi.id);
                              })
                            }
                            className="mt-5"
                          >
                            Uppdatera
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}
